/*
 * @Author: 张印 hbzyin@163.com
 * @Date: 2023-07-27 19:21:22
 * @LastEditors: Please set LastEditors
 * @LastEditTime: 2023-08-25 18:04:06
 * @FilePath: /code/mall-fe/src/comps/LocContextProvider.tsx
 * @Description:地区选择定位全局上下文
 *
 */
/* eslint-disable no-param-reassign */
import { getAreaTreeByGeo } from 'api/common';
import { getFirstTownOfCityCode } from 'api/user';
import useAreaCookie from 'hooks/useAreaCookie';
import { defaultCoordsStr, NO_LOC_HASH } from 'libs/cons';
import { LocContext, MediaQueryContext, SiteContext } from 'libs/contexts';
import { getCurrentPosition, getLocalStorage, setLocalStorage } from 'libs/utils';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Toast } from 'react-vant';

type LocContextProviderProps = {
  children: React.ReactNode;
};

export default function LocContextProvider({ children }: LocContextProviderProps) {
  const site = useContext(SiteContext);
  const { isMobile } = useContext(MediaQueryContext);
  // 地区选择变化时设置cookie
  const { AREA_CODE_KEY, AREA_SYS_NO_KEY, setCookie } = useAreaCookie();
  // 租户编号
  const tenantCode = site?.tenantCode ?? 'pazc';
  const LOCATION_KEY = `${tenantCode}_locInfo`;

  const [location, setLocation] = useState<LocaltionInfo>({
    sysNo: '',
    areaCode: '',
  });

  useEffect(() => {
    if (!isMobile) return;
    if (window.location.hash.includes(NO_LOC_HASH)) return;
    (async () => {
      if (!location.sysNo) {
        // TODO: 这里使用 localstorage 有待商榷
        const loc = getLocalStorage(LOCATION_KEY);
        if (loc) {
          // 从浏览器缓存，更新至组件状态
          setLocation({ ...loc });
          setCookie(AREA_SYS_NO_KEY, loc.sysNo);
          setCookie(AREA_CODE_KEY, loc.areaCode);
          return;
        }
        try {
          if (navigator.userAgent.indexOf('cctbsupplier/1') !== -1) {
            console.info(`商链通：使用默认的「经纬度」定位`);
            //商链通app中的严选直接走默认定位，避免申权限导致的隐私合规问题
            fetchAreaDataByGeo(defaultCoordsStr);
            return;
          }
          // 默认经纬度
          let locationStr: string = defaultCoordsStr;
          // 调用「浏览器」API获取地理位置，只在HTTPS场景下生效
          // TODO: 需要判断当前的环境，微信环境调用获取位置信息的API
          const pos = await getCurrentPosition();
          const { longitude, latitude } = pos?.coords;
          console.info(`浏览器定位成功：${longitude},${latitude}`);
          if (longitude && latitude) {
            locationStr = `${longitude},${latitude}`;
          }
          fetchAreaDataByGeo(locationStr);
        } catch (error) {
          console.log(`浏览器定位失败:${error}`);
          // 定位失败使用默认的「经纬度」定位
          fetchAreaDataByGeo(defaultCoordsStr);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.sysNo]);

  // 依据经纬度，
  async function fetchAreaDataByGeo(location: string) {
    try {
      const res = await getAreaTreeByGeo({
        location,
      });
      if (res?.data && res.data?.subAreas?.length) {
        buildOptions(res.data?.subAreas);
      } else {
        console.log('获取area失败:', location);
        // 递归中断的条件
        if (location === defaultCoordsStr) {
          Toast.fail('当前地区暂无可售商品');
        } else {
          fetchAreaDataByGeo(defaultCoordsStr);
        }
      }
    } catch (err) {
      Toast.fail(err.message);
    }
  }

  async function buildOptions(subAreas: Array<AreaItem> | null) {
    const newSelectAreas = [];
    // 找到集合中第一个有 subAreas的节点，递归递归找下去，路径存放在newSelectAreas当中
    while (subAreas != null) {
      let flag = true;
      for (const item of subAreas) {
        if (item.subAreas) {
          newSelectAreas.push(item);
        }
        if (item.subAreas) {
          subAreas = item.subAreas;
          flag = false;
          break;
        }
      }
      if (flag) {
        subAreas = null;
      }
    }
    if (!newSelectAreas.length) {
      // 联动 fetchAreaDataByGeo 递归调用
      return fetchAreaDataByGeo(defaultCoordsStr);
    }

    // fix 定位获取不到第三级区域
    // 只使用第二级数据
    const res = await getFirstTownOfCityCode(newSelectAreas[1]?.sysNo);
    const loc = {
      sysNo: res.data?.sysNo,
      areaCode: res.data?.levelCode,
    };
    setLocation({ ...loc });
    // 浏览器本地缓存
    setLocalStorage(LOCATION_KEY, loc);
    // 更新cookie 不确定哪些api会取cookie
    setCookie(AREA_SYS_NO_KEY, loc?.sysNo);
    setCookie(AREA_CODE_KEY, loc?.areaCode);
  }

  const locContextValue = useMemo(
    () => ({
      location,
      locKey: LOCATION_KEY,
      setLocation,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location],
  );

  return <LocContext.Provider value={locContextValue}>{children}</LocContext.Provider>;
}
